// src/store/slices/tasksSlice.js
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { taskService } from '../../services/taskService.js';
import { filterTasks, groupTasksByStatus, sortTasksByPriority } from '../../utils/taskUtils.js';
import { TASK_STATUS_ORDER } from '../../constants/task.js';
import { showToast } from './toastsSlice.js';

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchTasks = createAsyncThunk(
  'tasks/fetchAll',
  async (_, { rejectWithValue }) => {
    const { data, error } = await taskService.fetchAll();
    if (error) return rejectWithValue(error);
    return data;
  }
);

export const createTask = createAsyncThunk(
  'tasks/create',
  async (taskData, { dispatch, rejectWithValue }) => {
    const newTask = {
      ...taskData,
      id: uuidv4(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const { data, error } = await taskService.create(newTask);
    if (error) return rejectWithValue(error);
    dispatch(showToast('Task created', 'success'));
    return data;
  }
);

export const updateTask = createAsyncThunk(
  'tasks/update',
  async (taskData, { dispatch, rejectWithValue }) => {
    const updatedTask = { ...taskData, updatedAt: new Date().toISOString() };
    const { data, error } = await taskService.update(updatedTask);
    if (error) return rejectWithValue(error);
    dispatch(showToast('Task updated', 'success'));
    return data;
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/delete',
  async (taskId, { dispatch, rejectWithValue }) => {
    const { data, error } = await taskService.remove(taskId);
    if (error) return rejectWithValue(error);
    dispatch(showToast('Task deleted'));
    return data;
  }
);

export const moveTask = createAsyncThunk(
  'tasks/move',
  async ({ taskId, status }, { dispatch, rejectWithValue }) => {
    dispatch(optimisticMoveTask({ taskId, status }));
    const { data, error } = await taskService.updateStatus(taskId, status);
    if (error) return rejectWithValue(error);
    return data;
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  items: [],
  status: 'idle',
  error: null,
  activeTaskId: null,
  modalMode: null,
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    openCreateModal(state) {
      state.activeTaskId = null;
      state.modalMode = 'create';
    },
    openEditModal(state, action) {
      state.activeTaskId = action.payload;
      state.modalMode = 'edit';
    },
    closeModal(state) {
      state.activeTaskId = null;
      state.modalMode = null;
    },
    optimisticMoveTask(state, action) {
      const { taskId, status } = action.payload;
      const task = state.items.find((t) => t.id === taskId);
      if (task) {
        task.status = status;
        task.updatedAt = new Date().toISOString();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(fetchTasks.fulfilled, (state, action) => { state.status = 'succeeded'; state.items = action.payload; })
      .addCase(fetchTasks.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload ?? 'Failed to load tasks'; });

    builder
      .addCase(createTask.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.modalMode = null; state.activeTaskId = null;
      })
      .addCase(createTask.rejected, (state, action) => { state.error = action.payload ?? 'Failed to create task'; });

    builder
      .addCase(updateTask.fulfilled, (state, action) => {
        const idx = state.items.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
        state.modalMode = null; state.activeTaskId = null;
      })
      .addCase(updateTask.rejected, (state, action) => { state.error = action.payload ?? 'Failed to update task'; });

    builder
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.items = state.items.filter((t) => t.id !== action.payload);
        state.modalMode = null; state.activeTaskId = null;
      })
      .addCase(deleteTask.rejected, (state, action) => { state.error = action.payload ?? 'Failed to delete task'; });

    builder.addCase(moveTask.rejected, (state, action) => { state.error = action.payload ?? 'Failed to move task'; });
  },
});

export const { openCreateModal, openEditModal, closeModal, optimisticMoveTask } = tasksSlice.actions;
export default tasksSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectAllTasks = (state) => state.tasks.items;
export const selectTasksStatus = (state) => state.tasks.status;
export const selectTasksError = (state) => state.tasks.error;
export const selectModalMode = (state) => state.tasks.modalMode;
export const selectActiveTaskId = (state) => state.tasks.activeTaskId;

export const selectActiveTask = createSelector(
  selectAllTasks,
  selectActiveTaskId,
  (tasks, id) => (id ? tasks.find((t) => t.id === id) ?? null : null)
);

export const selectFilteredTasks = createSelector(
  selectAllTasks,
  (state) => state.filters,
  (tasks, filters) => filterTasks(tasks, filters)
);

export const selectGroupedTasks = createSelector(selectFilteredTasks, (tasks) => {
  const grouped = groupTasksByStatus(tasks, TASK_STATUS_ORDER);
  TASK_STATUS_ORDER.forEach((status) => { grouped[status] = sortTasksByPriority(grouped[status]); });
  return grouped;
});

export const selectTaskCounts = createSelector(selectAllTasks, (tasks) =>
  TASK_STATUS_ORDER.reduce((acc, status) => { acc[status] = tasks.filter((t) => t.status === status).length; return acc; }, {})
);

export const selectAllAssignees = createSelector(selectAllTasks, (tasks) =>
  [...new Set(tasks.map((t) => t.assignee).filter(Boolean))].sort()
);
